import { useContext, useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import LoadingContext from '../../_metronic/layout/core/Loading'
import { OrgContactInfo, useContactInfoReview } from '../hooks/useContactInfoReview'
import { useAuth } from '../modules/auth'
import { handlePatchRequest } from '../services'

interface ContactInfoForm {
  daglig_leder: string
  email: string
  mobile_phone: string
  invoice_email: string
  address_line1: string
  post_code: string
  city: string
  home_page: string
}

const emptyForm: ContactInfoForm = {
  daglig_leder: '',
  email: '',
  mobile_phone: '',
  invoice_email: '',
  address_line1: '',
  post_code: '',
  city: '',
  home_page: '',
}

function toForm(org: OrgContactInfo): ContactInfoForm {
  return {
    daglig_leder: org.daglig_leder ?? '',
    email: org.email ?? '',
    mobile_phone: org.mobile_phone ?? '',
    invoice_email: org.invoice_email ?? '',
    address_line1: org.address_line1 ?? '',
    post_code: org.post_code ?? '',
    city: org.city ?? '',
    home_page: org.home_page ?? '',
  }
}

/**
 * Every {@link CONTACT_REVIEW_INTERVAL_MONTHS} months, asks the org admin to
 * confirm (and optionally correct) the organisation's contact info. Mounted
 * once inside the authenticated area; renders nothing until a review is due.
 */
const ContactInfoReviewModal = () => {
  const intl = useIntl()
  const { currentUser } = useAuth()
  const { setLoading } = useContext(LoadingContext)
  const { show, org, hide } = useContactInfoReview()
  const [inputs, setInputs] = useState<ContactInfoForm>(emptyForm)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (org) setInputs(toForm(org))
  }, [org])

  if (!show || !org) return null

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setInputs((prev) => ({ ...prev, [name]: value }))
  }

  // "Remind later" just closes; the 6-month timer is NOT reset, so the prompt
  // reappears on the next session. The session flag prevents re-opening now.
  const handleRemindLater = () => hide()

  const handleConfirm = async () => {
    const orgId = currentUser?.user?.organisation_id?._id
    if (!orgId) return
    setSaving(true)
    // Dedicated endpoint: persists the edited fields AND stamps
    // contact_info_last_confirmed_at = now on the backend.
    const response = await handlePatchRequest(
      `/organisation/${orgId}/confirm-contact-info`,
      inputs,
      {},
      false
    )(setLoading)
    setSaving(false)
    if (response?.success) hide()
  }

  const field = (
    name: keyof ContactInfoForm,
    label: string,
    type: 'text' | 'email' | 'tel' = 'text'
  ) => (
    <div className='mb-5'>
      <label className='form-label fw-semibold fs-6'>{label}</label>
      <input
        type={type}
        name={name}
        value={inputs[name]}
        onChange={handleChange}
        className='form-control form-control-solid'
        style={{ borderRadius: '8px' }}
        disabled={saving}
      />
    </div>
  )

  return (
    <Modal show={show} onHide={handleRemindLater} size='lg' keyboard={false} centered>
      <Modal.Header closeButton>
        <Modal.Title>Stemmer kontaktinformasjonen?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className='text-muted mb-6'>
          Vi ber dere bekrefte at kontaktinformasjonen til organisasjonen er korrekt. Rett opp
          eventuelle feil og trykk «Bekreft».
        </p>
        {field('daglig_leder', intl.formatMessage({ id: 'general.manager.or.contact.person' }))}
        {field('email', intl.formatMessage({ id: 'email.general.manager' }), 'email')}
        {field('mobile_phone', intl.formatMessage({ id: 'mobile.general.manager' }), 'tel')}
        {field('invoice_email', intl.formatMessage({ id: 'email.invoice' }), 'email')}
        {field('home_page', intl.formatMessage({ id: 'website' }))}
        {field('address_line1', intl.formatMessage({ id: 'address' }))}
        <div className='row'>
          <div className='col-md-4'>{field('post_code', 'Postnummer')}</div>
          <div className='col-md-8'>{field('city', 'Poststed')}</div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type='button' className='btn btn-light me-3' onClick={handleRemindLater} disabled={saving}>
          Påminn meg senere
        </button>
        <button
          type='button'
          className='btn btn-lg btn-primary authbgcolor'
          onClick={handleConfirm}
          disabled={saving}
        >
          {!saving && 'Bekreft'}
          {saving && (
            <span className='indicator-progress' style={{ display: 'block' }}>
              {intl.formatMessage({ id: 'please.wait' })}...{' '}
              <span className='spinner-border spinner-border-sm align-middle ms-2' />
            </span>
          )}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export default ContactInfoReviewModal
